import React, { useRef, useEffect, useState, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Briefcase, MapPin, Globe, Phone, History, CalendarDays, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import ClientSettings from '@/components/clients/client_list/ClientSettings';
import { STAGE_LABELS, STAGE_COLORS, WARMTH_COLORS } from '@/components/clients/shared/stageConstants';
import { getWarmthLevel } from '@/types/client';

// Types based on ClientsPage state/data
import type { ClientDisplayData, ClientFullConfigData } from '@/pages/ClientsPage';

interface ClientCardProps {
  client: ClientDisplayData;
  clientId: string;
  isEditing: boolean;
  initialClientDataForEdit: ClientFullConfigData | null;
  onSaveEdit: (updatedData: ClientFullConfigData) => Promise<void>;
  onCancelEdit: () => void;
  validationError: string | null;
  onEditStart: (clientId: string) => void;
  onRemove: (clientId: string) => void;
  selectedClients: string[];
  setSelectedClients: React.Dispatch<React.SetStateAction<string[]>>;
  multiSelectEnabled: boolean;
  formLoading: boolean;
  formError: string | null;
}

// Memoized header to prevent re-rendering on tab change
const ClientCardHeader = memo(({
  client,
  isEditing,
  multiSelectEnabled,
  selectedClients,
  setSelectedClients,
  clientId,
}: {
  client: ClientDisplayData;
  isEditing: boolean;
  multiSelectEnabled: boolean;
  selectedClients: string[];
  setSelectedClients: React.Dispatch<React.SetStateAction<string[]>>;
  clientId: string;
}) => {
  const navigate = useNavigate();
  return (
    <div className={cn(
      "flex w-full",
      isEditing ? "flex-col" : "items-center justify-between"
    )}>
      <div className={cn(
        "flex flex-1",
        isEditing ? "w-full flex-col items-center" : "items-start overflow-hidden mr-2"
      )}>
        {multiSelectEnabled && !isEditing && (
          <div className="flex-shrink-0 mr-2 mt-1">
            <label htmlFor={`select-client-${clientId}`} className="sr-only">
              Select client {client.name}
            </label>
            <input
              type="checkbox"
              id={`select-client-${clientId}`}
              checked={selectedClients.includes(clientId)}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedClients(prev =>
                  e.target.checked ? [...prev, clientId] : prev.filter(id => id !== clientId)
                );
              }}
              className="h-4 w-4 rounded border-border focus:ring-primary cursor-pointer"
            />
          </div>
        )}
        <motion.div 
          layout="position"
          className={cn(
            "h-9 w-9 rounded bg-primary/10 flex items-center justify-center text-primary flex-shrink-0",
            isEditing ? "mb-2" : "mr-3 self-center"
          )}
        >
          <User className="h-5 w-5" />
        </motion.div>
        <div className={cn(
          "overflow-hidden",
          isEditing ? "w-full text-center" : "flex-1"
        )}>
          <motion.div 
            layout
            className={cn(
              "font-heading uppercase tracking-wider text-foreground",
              isEditing ? "text-lg" : "truncate"
            )}
          >
            {client.name}
          </motion.div>
          <motion.div 
            layout
            className={cn(
              "text-xs text-muted-foreground",
              isEditing 
                ? "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-left max-w-md mx-auto" 
                : "space-y-0.5 mt-0.5"
            )}
          >
            {client.domain && (
              <div className="flex items-center truncate">
                <Globe className="h-3 w-3 mr-1.5 flex-shrink-0" />
                <span className="truncate">{client.domain}</span>
              </div>
            )}
            {client.physical_address && (
              <div className="flex items-center truncate">
                <MapPin className="h-3 w-3 mr-1.5 flex-shrink-0" />
                <span className="truncate">{client.physical_address}</span>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center">
                <Phone className="h-3 w-3 mr-1.5 flex-shrink-0" />
                <span className="truncate">{client.phone}</span>
              </div>
            )}
            <div className="flex items-center truncate">
              <Briefcase className="h-3 w-3 mr-1.5 flex-shrink-0" />
              <span className="truncate">Active Bookings: <span className="font-mono">{client.activeBookings}</span></span>
            </div>
            <div className="flex items-center truncate">
              <History className="h-3 w-3 mr-1.5 flex-shrink-0" />
              <span className="truncate">Previous Bookings: <span className="font-mono">{client.previousBookings}</span></span>
            </div>
            <div className="flex items-center truncate">
              <CalendarDays className="h-3 w-3 mr-1.5 flex-shrink-0" />
              <span className="truncate">Last Active: <span className="font-mono">{client.lastBooking || 'N/A'}</span></span>
            </div>
          </motion.div>
          {/* CRM indicators */}
          {!isEditing && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {/* Stage badge */}
              {client.stage && (() => {
                const stageColors = STAGE_COLORS[client.stage];
                return (
                  <span className={cn(
                    'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-label uppercase tracking-wider',
                    stageColors.bg, stageColors.text
                  )}>
                    <span className={cn('w-1 h-1 rounded-full', stageColors.dot)} />
                    {STAGE_LABELS[client.stage]}
                  </span>
                );
              })()}
              {/* Warmth dot */}
              {client.healthMetrics && (() => {
                const warmth = getWarmthLevel(client.healthMetrics.warmth_ratio);
                const warmthColors = WARMTH_COLORS[warmth];
                return (
                  <span className={cn(
                    'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-label uppercase tracking-wider',
                    warmthColors.bg, warmthColors.text
                  )}>
                    <span className={cn('w-1 h-1 rounded-full', warmthColors.dot)} />
                    {warmth}
                  </span>
                );
              })()}
              {/* Health score */}
              {client.healthMetrics?.health_score != null && (
                <span className={cn(
                  'font-mono text-[10px] px-1.5 py-0.5 rounded bg-surface-2',
                  client.healthMetrics.health_score >= 4.0 ? 'text-led-green' :
                  client.healthMetrics.health_score >= 3.0 ? 'text-led-amber' : 'text-led-red'
                )}>
                  {client.healthMetrics.health_score.toFixed(1)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {!isEditing && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
            aria-label={`View ${client.name} profile`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/clients/${clientId}`);
            }}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
});
ClientCardHeader.displayName = 'ClientCardHeader';

const ClientCard: React.FC<ClientCardProps> = ({
  client,
  clientId,
  isEditing,
  initialClientDataForEdit,
  onSaveEdit,
  onCancelEdit,
  validationError,
  onEditStart,
  onRemove,
  selectedClients,
  setSelectedClients,
  multiSelectEnabled,
  formLoading,
  formError,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [localFormData, setLocalFormData] = useState<ClientFullConfigData | null>(null);

  useEffect(() => {
    if (isEditing && initialClientDataForEdit) {
      setLocalFormData(initialClientDataForEdit);
    } else if (!isEditing) {
      setLocalFormData(null);
    }
  }, [isEditing, initialClientDataForEdit]);

  const handleSettingsUpdate = useCallback((updates: Partial<ClientFullConfigData>) => {
    setLocalFormData(prev => {
      if (!prev) return null; // Should not happen if editing
      return { ...prev, ...updates };
    });
  }, []);

  const handleSaveClick = useCallback(() => {
    if (localFormData) {
      onSaveEdit(localFormData);
    }
  }, [localFormData, onSaveEdit]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (multiSelectEnabled) {
      if (e.target === ref.current || (e.target instanceof Element && !e.target.closest('button, input[type="checkbox"]'))) {
        setSelectedClients(prev =>
          prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
        );
      }
    } else {
      const isInteractiveElement = e.target instanceof Element &&
                                 (e.target.closest('button') ||
                                  e.target.closest('input[type="checkbox"]'));
      if (!isInteractiveElement) {
        onEditStart(clientId);
      }
    }
  }, [multiSelectEnabled, clientId, setSelectedClients, onEditStart]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (multiSelectEnabled) {
        setSelectedClients(prev =>
          prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
        );
      } else {
        onEditStart(clientId);
      }
    }
  }, [multiSelectEnabled, clientId, setSelectedClients, onEditStart]);

  const handleSaveButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    handleSaveClick();
  }, [handleSaveClick]);

  const handleCloseButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCancelEdit();
  }, [onCancelEdit]);

  return (
    <motion.div
      layout
      layoutId={`client-${clientId}`}
      ref={ref}
      role={!isEditing ? "button" : undefined}
      tabIndex={!isEditing ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={!isEditing ? handleKeyDown : undefined}
      className={cn(
        "relative flex flex-col p-3 border rounded transition-colors",
        'border-border',
        isEditing ? "col-span-full" : "",
        !isEditing ? 'cursor-pointer hover:bg-accent/20' : '',
        selectedClients.includes(clientId) && multiSelectEnabled ? 'border-primary/50 ring-1 ring-primary/50 bg-primary/5' : '',
        "text-sm md:text-base"
      )}
      transition={{
        type: "tween",
        ease: "easeInOut",
        duration: 0.2
      }}
      style={{ cursor: isEditing ? 'default' : 'pointer' }}
    >
      <ClientCardHeader
        client={client}
        isEditing={isEditing}
        multiSelectEnabled={multiSelectEnabled}
        selectedClients={selectedClients}
        setSelectedClients={setSelectedClients}
        clientId={clientId}
      />

      <AnimatePresence mode="wait" initial={false}>
        {isEditing && (
          <motion.div
            key="expanded-content"
            className="w-full mt-4 pt-4 border-t border-border overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0 }}
            transition={{ 
              opacity: { duration: 0.2 },
              height: { duration: 0.3, delay: 0.2 },
              ease: "easeInOut"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                {localFormData ? (
                  <ClientSettings
                    clientData={localFormData}
                    onUpdate={handleSettingsUpdate}
                    isLoading={formLoading}
                  />
                ) : (
                  <p className="text-center text-muted-foreground">Loading settings...</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                onClick={handleSaveButtonClick}
                disabled={formLoading || !localFormData}
              >
                Save
              </Button>
              <Button 
                variant="outline" 
                onClick={handleCloseButtonClick} 
                disabled={formLoading}
              >
                Close
              </Button>
            </div>
            {formError && <p className="text-sm text-destructive mt-2 text-right">{formError}</p>}
            {validationError && <p className="text-sm text-destructive mt-2 text-right">{validationError}</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default memo(ClientCard);
